import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { api } from '../../common';
import { UserUIModel } from '../models/user.model';
import { BehaviorSubject, catchError, switchMap, take, tap } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { environment } from '../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { ErrorHandlerService } from './error-handler.service';
import { ClientDiagnosticsService } from './client-diagnostics.service';
import { recordRumEvent } from '../monitoring/aws-rum';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private baseUrl = `${api}/users`;

  private user$ = new BehaviorSubject<UserUIModel | undefined>(undefined);

  constructor(
    private http: HttpClient,
    private authService: OidcSecurityService,
    private cookieService: CookieService,
    private errorService: ErrorHandlerService,
    private clientDiagnostics: ClientDiagnosticsService,
  ) {}

  // Returns either stored user data or loads it from the API
  public getUser() {
    return this.user$.value
      ? this.user$.asObservable()
      : this.loadUserData().pipe(catchError((e) => this.errorService.handleError(e, 'User Data')));
  }

  logout() {
    recordRumEvent('auth.logout.requested', {
      app: 'rep-flow',
      source: 'user_service',
      hasSessionStorage: !!window.sessionStorage,
    });

    this.clientDiagnostics.track(
      'auth.logout.requested',
      {
        hasSessionStorage: !!window.sessionStorage,
      },
      'info',
    );
    this.clientDiagnostics.flushNow();

    this.user$.next(undefined);
    if (window.sessionStorage) {
      window.sessionStorage.clear();
    }
    this.cookieService.deleteAll();

    this.authService
      .logoff()
      .pipe(take(1))
      .subscribe(() => {
        // Cognito Logout URL
        const clientId = environment.cognitoClientId;
        const logoutUri = encodeURIComponent(window.location.origin);
        const cognitoDomain = environment.cognitoDomain;

        window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${logoutUri}`;
      });
  }

  private getUserData(id: string) {
    return this.http.get<UserUIModel>(`${this.baseUrl}/${id}`).pipe(tap((data) => this.user$.next(data)));
  }

  private loadUserData() {
    return this.authService.getUserData().pipe(switchMap(({ username }) => this.getUserData(username)));
  }
}
